import { useEffect, useCallback, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useInitializeApp from 'hooks/useInitializeApp';
import { setRefreshAfterPaymentFlag } from 'reducers/ui/uiSlice';
import { ROUTES_SELF_SERVICE } from 'shared/constants/selfServiceRoutes';
import { getPaymentStatus, getPaymentReceipt } from 'services/ApiService';
import { getDocumentUrl } from 'services/CommonService';
import DefaultHeader from 'components/pageLayout/DefaultHeader';
import Layout from 'components/pageLayout/Layout';
import PageContentContainer from 'components/pageLayout/PageContentContainer';
import Button from 'components/Button';
import Text from 'components/typography/Text';
import { BUTTON_TYPE_LINK } from 'shared/constants/common';
import Spinner from 'components/Spinner';

export default function PaymentStatusPage() {
  const { selectedBooking, loginCredentials, textConfig = {} } = useSelector(
    (state) => state.ui,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingReceipt, setIsLoadingReceipt] = useState(true);
  const dispatch = useDispatch();
  useInitializeApp(loginCredentials, dispatch);
  const initialTabSelectionDone = useRef(false);
  const [message, setMessage] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [receipt, setReceipt] = useState({});
  const [isLoyaltyBooking, setIsLoyaltyBooking] = useState(false);
  
  const getParamFromUrl = (param) => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get(param);
  };

  const setPaymentMessage = useCallback(({ data: { paymentStatus: status } = {} }) => {
    setPaymentStatus(status);
    switch (status) {
      case 'successful':
        setMessage('Thank you, your payment was successful!');
        break;
      case 'pending':
        setMessage('Your payment is pending. Please contact your bank for further information.');
        break;
      case 'failed':
        setMessage('Sorry, your payment was unsuccessful!');
        break;
      case 'aborted':
        setMessage('Payment cancelled.');
        break;
      default:
        setMessage('Payment status unknown.');
    }
  }, []);

  const setPaymentReceipt = useCallback((data) => {
    setReceipt(data.data[0]);
  }, []);

  const loadPaymentReceipt = useCallback(async (transactionGuid, bookingRef) => {
    try {
      const paymentReceipt = await getPaymentReceipt(transactionGuid, bookingRef);
      setPaymentReceipt(paymentReceipt);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingReceipt(false);
    }
  }, [setPaymentReceipt]);

  const loadPaymentStatus = useCallback(async (transactionGuid) => {
    try {
      const data = await getPaymentStatus(transactionGuid);
      setPaymentMessage(data);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [setPaymentMessage]);
  
  useEffect(() => {
    initialTabSelectionDone.current = false;
    const transactionGuid = getParamFromUrl('transactionGuid');
    if (transactionGuid) {
      loadPaymentStatus(transactionGuid);
    }
  }, [loadPaymentStatus]);

  useEffect(() => {
    const transactionGuid = getParamFromUrl('transactionGuid');
    if (transactionGuid && !isLoading && paymentStatus === 'successful') {
      loadPaymentReceipt(transactionGuid, selectedBooking.reference);
      dispatch(setRefreshAfterPaymentFlag(true));
    } else if (!isLoading && paymentStatus !== 'successful') {
      setIsLoadingReceipt(false);
    }
  }, [dispatch, loadPaymentReceipt, isLoading, paymentStatus, selectedBooking.reference]);

  useEffect(() => {
    if (selectedBooking?.tours?.length) {
      const foundLoyalty = selectedBooking.tours.some(tour => tour.loyalty === true);
      setIsLoyaltyBooking(foundLoyalty);
    } else {
      setIsLoyaltyBooking(false);
    }
  }, [selectedBooking]);

  return (
    <Layout>
      <DefaultHeader
        label={selectedBooking?.name || `Your Booking`}
        departureDate={isLoyaltyBooking ? null : selectedBooking?.outbound}
      />
      <PageContentContainer>
        <div className='flex items-start pt-6 pb-6'>
          {(isLoading || isLoadingReceipt) ? (
            <Spinner className='flex flex-col pt-6 pb-6 w-full md:w-9/12 m-h-[150px]'/>
          ) : (
            <div className='flex flex-col pt-6 pb-6 w-full md:w-9/12'>
              <h1 className='ml-[20px] mt-6 text-3xl font-medium'>{message}</h1>

              {paymentStatus === 'successful' && textConfig?.paymentSuccessfulText && (
                <div className='ml-[20px] mt-4 text-gray-600'>
                  {textConfig.paymentSuccessfulText}
                </div>
              )}

              {receipt && (
                <div className='flex justify-between py-3 md:flex-row flex-col ml-[20px] mt-6'>
                  <Text className="mr-4 text-gray-500 md:text-black">{receipt.name}</Text>
                  <a href = {getDocumentUrl(receipt, selectedBooking.reference)}
                  rel = "noreferrer"
                  target = "_blank"
                  className = "bold underline"
                  >
                    {receipt.fileName}
                  </a>
                </div>
              )}

              <Button
                className='ml-[20px] my-6 mr-[20px] w-[200px]'
                label='Return to booking'
                type={BUTTON_TYPE_LINK}
                url={`${ROUTES_SELF_SERVICE.BOOKINGS}/${selectedBooking.id || selectedBooking.reference}`}
              />
            </div>
          )}
        </div>
      </PageContentContainer>
    </Layout>
  );
}
